// TransactionStatusBadge — DRY: one payment-status pill for the student, admin and tutor pages.
// Students and admins see the payment's own status; tutors (read-only) get wording about what it means
// for their class, a hover hint, and a "No payment" state for students without a payment record.
// (PaymentStatusBadge is the enrollment-level paid/partial/pending pill; this one is per payment.)
import Badge from "./Badge.js";
import type { TransactionStatus } from "@dm-lms/shared/types";

type Tone = "green" | "gold" | "red" | "gray";

interface View {
  label: string;
  tone:  Tone;
  hint?: string;
}

// student: the payments table; card: the badge under an enrolled course card (louder, plus "no record");
// tutor: read-only roster wording with hover hints.
type TransactionStatusBadgeProps =
  | { audience?: "student"; status: TransactionStatus; hasReference?: boolean }
  | { audience: "card"; status: TransactionStatus | null; hasReference?: boolean }
  | { audience: "tutor"; status: TransactionStatus | null; hasReference?: boolean; paymentCode?: string | undefined };

// A pending payment with a submitted code is awaiting verification
function describeForStudent(status: TransactionStatus, hasReference: boolean): View {
  switch (status) {
    case "success":  return { label: "Paid",     tone: "green" };
    case "failed":   return { label: "Failed",   tone: "red" };
    case "refunded": return { label: "Refunded", tone: "gray" };
    case "pending":  return { label: hasReference ? "Awaiting verification" : "Pending", tone: "gold" };
  }
}

// A pending payment with no code yet is red: the student still has something to do
function describeForCard(status: TransactionStatus | null, hasReference: boolean): View {
  if (status === null) return { label: "No payment record", tone: "red" };
  switch (status) {
    case "success":  return { label: "Paid",     tone: "green" };
    case "failed":   return { label: "Payment failed", tone: "red" };
    case "refunded": return { label: "Refunded", tone: "gray" };
    case "pending":  return hasReference
      ? { label: "Awaiting verification", tone: "gold" }
      : { label: "Payment pending", tone: "red" };
  }
}

function describeForTutor(status: TransactionStatus | null, hasReference: boolean, paymentCode?: string): View {
  if (status === null) {
    return { label: "No payment", tone: "red", hint: "No payment record found — contact admin" };
  }
  switch (status) {
    case "success":
      return { label: "Paid", tone: "green", hint: "Payment verified" };
    case "failed":
      return {
        label: "Failed — resubmitting",
        tone: "red",
        hint: "Payment verification failed — student resubmitting",
      };
    case "refunded":
      return { label: "Refunded", tone: "gray", hint: "Payment was refunded" };
    case "pending":
      return hasReference
        ? {
            label: "Awaiting verification",
            tone: "gold",
            hint: `Student submitted code${paymentCode ? ` ${paymentCode}` : ""} — awaiting admin verification`,
          }
        : { label: "Payment pending", tone: "red", hint: "Student has not submitted payment code yet" };
  }
}

export default function TransactionStatusBadge(props: TransactionStatusBadgeProps) {
  const hasReference = props.hasReference ?? false;
  const view =
    props.audience === "tutor"
      ? describeForTutor(props.status, hasReference, props.paymentCode)
      : props.audience === "card"
        ? describeForCard(props.status, hasReference)
        : describeForStudent(props.status, hasReference);

  return (
    <Badge tone={view.tone} title={view.hint}>
      {view.label}
    </Badge>
  );
}
